/**
 * Formatierung und Farbregeln der Verkaufsstatistik - zentral an einer
 * Stelle, damit Zahlen, Daten und Warengruppen-Farben auf der gesamten
 * Seite identisch aussehen.
 *
 * Die kategoriale Palette ist fest vorgegeben (auf Farbfehlsichtigkeit und
 * Kontrast geprüft) und wird NICHT verändert oder erweitert. Gibt es mehr
 * Warengruppen als Farben, werden die kleinsten zu "Sonstige"
 * zusammengefasst (siehe buildGroupColorMap/bucketRowsByGroup).
 */
export const GROUP_PALETTE = ["#2f6fd0", "#e8590c", "#9c36b5", "#2f9e44", "#00a0b8", "#d6336c", "#9a6a00"];
export const OTHER_GROUP_COLOR = "#868e96";
export const OTHER_GROUP_LABEL = "Sonstige";

// Diverging-Skala für Zu-/Abnahmen: zwei Farben mit neutralem Grau in der
// Mitte - keine Regenbogenpalette.
export const TREND_UP_COLOR = "#2f9e44";
export const TREND_DOWN_COLOR = "#e8590c";
export const TREND_NEUTRAL_COLOR = "#868e96";

// ABC-Klassen sind keine Warengruppen - sie dürfen die ersten drei Farben
// der Palette dauerhaft und konsistent (Pareto-Karten, Detailtabelle,
// Ladenhüter-Tabelle) für sich verwenden.
export const ABC_COLORS = {A: GROUP_PALETTE[0], B: GROUP_PALETTE[1], C: GROUP_PALETTE[2]};

const numberFormatter = new Intl.NumberFormat("de-DE");
const currencyFormatter = new Intl.NumberFormat("de-DE", {style: "currency", currency: "EUR"});
const currencyCompactFormatter = new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    notation: "compact",
    maximumFractionDigits: 1,
});

export function formatNumber(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return numberFormatter.format(value);
}

/**
 * Stückzahlen (Dosen, Marker) gibt es nur als ganze Zahl - anders als
 * formatNumber() rundet formatQuantity deshalb kaufmännisch auf ganze
 * Zahlen (Math.round, für die hier ausschließlich nicht-negativen Mengen
 * gleichbedeutend mit "halbe rundet auf"), bevor mit deutschem
 * Tausenderpunkt formatiert wird.
 */
export function formatQuantity(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return numberFormatter.format(Math.round(Number(value)));
}

export function formatEuro(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return currencyFormatter.format(value);
}

export function formatEuroCompact(value) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return currencyCompactFormatter.format(value);
}

export function formatPercent(value, decimals = 1) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "–";
    }
    return `${new Intl.NumberFormat("de-DE", {minimumFractionDigits: decimals, maximumFractionDigits: decimals}).format(value)} %`;
}

/** Veränderungswerte tragen immer ein explizites Vorzeichen - Farbe allein
 * darf laut Vorgabe nie der einzige Träger der Information sein. */
export function formatSignedPercent(value, decimals = 1) {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return null;
    }
    const sign = value > 0 ? "+" : value < 0 ? "" : "±";
    return `${sign}${new Intl.NumberFormat("de-DE", {minimumFractionDigits: decimals, maximumFractionDigits: decimals}).format(value)} %`;
}

/** "2026-07" -> "Jul 2026"; "2026" bleibt unverändert. */
export function formatMonthPeriod(period) {
    if (!period) {
        return "–";
    }
    if (/^\d{4}$/.test(period)) {
        return period;
    }
    const [year, month] = String(period).split("-").map(Number);
    if (!year || !month) {
        return period;
    }
    return new Date(year, month - 1, 1).toLocaleDateString("de-DE", {month: "short", year: "numeric"});
}

export function formatDateDE(isoDate) {
    if (!isoDate) {
        return "–";
    }
    const [y, m, d] = String(isoDate).split("-").map(Number);
    if (!y || !m || !d) {
        return isoDate;
    }
    return `${String(d).padStart(2, "0")}.${String(m).padStart(2, "0")}.${y}`;
}

export function toISODate(year, month, day) {
    return `${String(year).padStart(4, "0")}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

export function addMonthsISO(isoDate, months) {
    const [y, m, d] = isoDate.split("-").map(Number);
    const date = new Date(y, m - 1 + months, d);
    return toISODate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

/**
 * Erkennt, ob ein gewählter Zeitraum den Jahreswechsel 2020/2021 überspannt
 * - genau der Zeitpunkt, an dem Weclapp die Verkäufe von der Übernummer auf
 * die Einzelfarbe umgebucht hat. Ein Zeitraum, der auf BEIDEN Seiten dieses
 * Stichtags liegt, zeigt auf Artikel-Ebene einen scheinbaren Absturz, der
 * reines Buchungsartefakt ist (siehe Hinweis in FilterBar.jsx). Reine
 * String-Vergleiche reichen bei ISO-Datumsformaten ("YYYY-MM-DD") aus.
 */
export function spansBookingCutover(dateFrom, dateTo) {
    if (!dateFrom || !dateTo) {
        return false;
    }
    return dateFrom <= "2020-12-31" && dateTo >= "2021-01-01";
}

/**
 * Weist den (nach Umsatz) wichtigsten Warengruppen dauerhaft je eine feste
 * Farbe zu. `rankedGroupNames` muss unabhängig vom aktuellen Zeitraum-/
 * Warengruppenfilter ermittelt sein (siehe Statistics/Index.jsx) - sonst
 * würde ein Filter die Zuordnung verschieben, was ausdrücklich verboten ist.
 */
export function buildGroupColorMap(rankedGroupNames) {
    const map = {};
    (rankedGroupNames || []).slice(0, GROUP_PALETTE.length).forEach((name, index) => {
        if (name) {
            map[name] = GROUP_PALETTE[index];
        }
    });
    return map;
}

export function colorForGroup(colorMap, groupName) {
    if (groupName && colorMap && colorMap[groupName]) {
        return colorMap[groupName];
    }
    return OTHER_GROUP_COLOR;
}

/**
 * Fasst alle Zeilen, deren Warengruppe keine feste Farbe hat, zu einer
 * einzelnen "Sonstige"-Zeile zusammen (numerische Felder werden summiert).
 */
export function bucketRowsByGroup(rows, colorMap, {groupKey = "article_group", numericKeys} = {}) {
    const known = [];
    let other = null;
    (rows || []).forEach((row) => {
        const group = row[groupKey];
        if (group && colorMap && colorMap[group]) {
            known.push(row);
            return;
        }
        if (!other) {
            other = {...row, [groupKey]: OTHER_GROUP_LABEL};
            return;
        }
        const keys = numericKeys || Object.keys(row).filter((key) => typeof row[key] === "number");
        keys.forEach((key) => {
            other[key] = (other[key] || 0) + (row[key] || 0);
        });
    });
    if (other) {
        known.push(other);
    }
    return known;
}

function hexToRgb(hex) {
    const value = hex.replace("#", "");
    const bigint = parseInt(value, 16);
    return {r: (bigint >> 16) & 255, g: (bigint >> 8) & 255, b: bigint & 255};
}

function rgbToHex({r, g, b}) {
    return `#${[r, g, b].map((c) => Math.max(0, Math.min(255, Math.round(c))).toString(16).padStart(2, "0")).join("")}`;
}

/**
 * Einfarbige Abstufung hell->dunkel für Rangfolgen einer einzelnen Größe
 * (z. B. Top-Artikel nach Umsatz) - Index 0 (größter Wert) erhält die
 * kräftigste, der letzte Index die hellste Ausprägung.
 */
export function monochromeScale(baseColor, count, {lightest = 0.82, darkest = 0} = {}) {
    const base = hexToRgb(baseColor);
    const white = {r: 255, g: 255, b: 255};
    const steps = Math.max(count, 1);
    return Array.from({length: steps}, (_, index) => {
        const t = steps === 1 ? darkest : darkest + ((lightest - darkest) * index) / (steps - 1);
        return rgbToHex({
            r: base.r + (white.r - base.r) * t,
            g: base.g + (white.g - base.g) * t,
            b: base.b + (white.b - base.b) * t,
        });
    });
}
